import mapValues from 'lodash.mapvalues';
import mapKeys from 'lodash.mapkeys';
import invertBy from 'lodash.invertby';
import get from 'lodash.get';

import {
    BURST,
    DIBELS8,
    R3D,
    R3DSPANISH,
    IDEL2,
    IDEL,
    OLS,
    MATHCBM,
    OLS_SPANISH,
} from './applications';
import { BM_PERIODS, GRADE, PERIOD } from './enrollment';

export const ASSESSMENT_TYPE = {
    BM: 'BM', // Benchmark
    PM: 'PM', // Progress Monitoring
};

export const ASSESSMENT_IMPORTER_DATA_SOURCE = 'assessment-importer';

export const LECTURA_DATA_SOURCE_RESULTS_SCRIPT = 'results-script';

export const ASSESSMENT_TYPE_LABELS = {
    [ASSESSMENT_TYPE.BM]: 'Benchmark',
    [ASSESSMENT_TYPE.PM]: 'Progress',
};

export const ORF_MEASURE_SCORES = {
    FLUENCY: 'ORF',
    ACCURACY: 'ORF-ACC',
};

export const MEASURE_NAMES = {
    LNF: 'LNF',
    PSF: 'PSF',
    NWF: 'NWF',
    WRF: 'WRF',
    ORF: 'ORF',
    NWF_CLS: 'NWF-CLS',
    NWF_WRC: 'NWF-WRC',
    MAZE: 'MAZE',
    TRC: 'TRC',
    OL: 'OL',
    DEC_IW: 'DEC-IW',
    DEC_RW: 'DEC-RW',
    DEC_LC: 'DEC-LC',
    DEC_AP: 'DEC-AP',
    CS: 'CS',
    CS_PASSAGE: 'CS-Passage',
    CS_RETELL: 'CS-Retell',
    CS_RECALL: 'CS-Recall',
    CS_LITERAL: 'CS-Literal',
    CS_INFERENCE: 'CS-Inference',
    ORF_ACC: 'ORF-ACC',
    VOC: 'VOC',
    FNL: 'FNL',
    FNL2: 'FNL2',
    FSLI: 'FSLI',
    FSLIK: 'FSLIK',
    FSL: 'FSL',
    FSS: 'FSS',
    QQ: 'QQ',
    FEP: 'FEP',
    FLO: 'FLO',
    FLO_ACC: 'FLO-ACC',
    PAB: 'PAB',
    FSI: 'FSI',
    LSS: 'LSS',
    CP: 'CLPL',
};

export const SKILL_NAMES = {
    LETTER_NAMES: 'Letter Names',
    PHONEMIC_AWARENESS: 'Phonemic Awareness',
    LETTER_SOUNDS: 'Letter Sounds',
    DECODING: 'Decoding',
    WORD_READING: 'Word Reading',
    READING_FLUENCY: 'Reading Fluency',
    READING_ACCURACY: 'Reading Accuracy',
    BASIC_COMPREHENSION: 'Basic Comprehension',
    TEXT_READING_COMPREHENSION: 'Text Reading Comprehension',
    IRREGULAR_WORDS: 'Irregular Words',
    REGULAR_WORDS: 'Regular Words',
    LETTER_COMBINATIONS: 'Letter Combinations',
    ADVANCED_PHONEMES: 'Advanced Phonics',
    RETELL_RECALL: 'Retell / Recall',
    LITERAL: 'Literal',
    INFERENCE: 'Inference',
    VOCABULARY: 'Vocabulary',
    ORAL_LANGUAGE: 'Oral Language',
    LISTENING_COMPREHENSION: 'Listening Comprehension',
    NOMBRAR_LETRAS: 'Nombrar letras',
    SONIDOS_DE_LETRAS: 'Sonidos de letras',
    CONCIENCIA_FONOLOGICA: 'Conciencia fonológica',
    DECODIFICATION: 'Decodificación',
    LECTURA_DE_PALABRAS: 'Lectura de palabras',
    FLUIDEZ_EN_LA_LECTURA: 'Fluidez en la lectura',
    PRECISION_EN_LA_LECTURA: 'Precisión en la lectura',
    PHONOLOGICAL_AWARENESS_BLENDING: 'PA Blending',
    FIRST_SOUND_ISOLATION: 'First Sound Isolation',
    COMPRENSION_BASICA: 'Comprensión básica',
};

export const SKILL_DISPLAY_NAMES = {
    ...mapKeys(SKILL_NAMES),
    [SKILL_NAMES.RETELL_RECALL]: 'Comp. Skills',
    [SKILL_NAMES.LITERAL]: 'Comp. Skills',
    [SKILL_NAMES.INFERENCE]: 'Comp. Skills',
    [SKILL_NAMES.PHONOLOGICAL_AWARENESS_BLENDING]: 'PA Blending',
};

export const SKILL_NAMES_BY_APPLICATION = {
    [DIBELS8]: [
        SKILL_NAMES.LETTER_NAMES,
        SKILL_NAMES.PHONEMIC_AWARENESS,
        SKILL_NAMES.FIRST_SOUND_ISOLATION,
        SKILL_NAMES.LETTER_SOUNDS,
        SKILL_NAMES.DECODING,
        SKILL_NAMES.WORD_READING,
        SKILL_NAMES.READING_FLUENCY,
        SKILL_NAMES.READING_ACCURACY,
        SKILL_NAMES.BASIC_COMPREHENSION,
        SKILL_NAMES.PHONOLOGICAL_AWARENESS_BLENDING,
    ],
    [R3D]: [SKILL_NAMES.TEXT_READING_COMPREHENSION],
    [R3DSPANISH]: [SKILL_NAMES.TEXT_READING_COMPREHENSION],
    [OLS]: [SKILL_NAMES.ORAL_LANGUAGE],
    [OLS_SPANISH]: [SKILL_NAMES.ORAL_LANGUAGE],
    [BURST]: [
        SKILL_NAMES.IRREGULAR_WORDS,
        SKILL_NAMES.REGULAR_WORDS,
        SKILL_NAMES.LETTER_COMBINATIONS,
        SKILL_NAMES.ADVANCED_PHONEMES,
        SKILL_NAMES.RETELL_RECALL,
        SKILL_NAMES.LITERAL,
        SKILL_NAMES.INFERENCE,
        SKILL_NAMES.VOCABULARY,
    ],
    [IDEL2]: [
        SKILL_NAMES.NOMBRAR_LETRAS,
        SKILL_NAMES.SONIDOS_DE_LETRAS,
        SKILL_NAMES.DECODIFICATION,
        SKILL_NAMES.CONCIENCIA_FONOLOGICA,
        SKILL_NAMES.LECTURA_DE_PALABRAS,
        SKILL_NAMES.FLUIDEZ_EN_LA_LECTURA,
        SKILL_NAMES.PRECISION_EN_LA_LECTURA,
    ],
    [IDEL]: [],
};

export const SKILL_AND_MEASURE_NAMES_BY_APPLICATION = {
    [IDEL2]: [
        { skillName: SKILL_NAMES.NOMBRAR_LETRAS, measureName: MEASURE_NAMES.FNL },
        { skillName: SKILL_NAMES.SONIDOS_DE_LETRAS, measureName: MEASURE_NAMES.FSLIK },
        { skillName: SKILL_NAMES.SONIDOS_DE_LETRAS, measureName: MEASURE_NAMES.FSL },
        { skillName: SKILL_NAMES.DECODIFICATION, measureName: MEASURE_NAMES.LSS },
        { skillName: SKILL_NAMES.CONCIENCIA_FONOLOGICA, measureName: MEASURE_NAMES.FSS },
        { skillName: SKILL_NAMES.CONCIENCIA_FONOLOGICA, measureName: MEASURE_NAMES.QQ },
        {
            skillName: SKILL_NAMES.LECTURA_DE_PALABRAS,
            measureName: MEASURE_NAMES.FEP,
        },
        { skillName: SKILL_NAMES.FLUIDEZ_EN_LA_LECTURA, measureName: MEASURE_NAMES.FLO },
    ],
};

export const PM_SKILL_AND_MEASURE_NAMES_BY_APPLICATION = {
    [DIBELS8]: [
        { skillName: SKILL_NAMES.PHONEMIC_AWARENESS, measureName: MEASURE_NAMES.PSF },
        { skillName: SKILL_NAMES.LETTER_SOUNDS, measureName: MEASURE_NAMES.NWF },
        { skillName: SKILL_NAMES.WORD_READING, measureName: MEASURE_NAMES.WRF },
        { skillName: SKILL_NAMES.READING_FLUENCY, measureName: MEASURE_NAMES.ORF },
    ],
    [R3D]: [{ skillName: SKILL_NAMES.TEXT_READING_COMPREHENSION, measureName: MEASURE_NAMES.TRC }],
    [R3DSPANISH]: [
        { skillName: SKILL_NAMES.TEXT_READING_COMPREHENSION, measureName: MEASURE_NAMES.TRC },
    ],
    [BURST]: [
        { skillName: SKILL_NAMES.IRREGULAR_WORDS, measureName: MEASURE_NAMES.DEC_IW },
        { skillName: SKILL_NAMES.REGULAR_WORDS, measureName: MEASURE_NAMES.DEC_RW },
        { skillName: SKILL_NAMES.LETTER_COMBINATIONS, measureName: MEASURE_NAMES.DEC_LC },
        { skillName: SKILL_NAMES.ADVANCED_PHONEMES, measureName: MEASURE_NAMES.DEC_AP },
        { skillName: SKILL_NAMES.RETELL_RECALL, measureName: MEASURE_NAMES.CS_PASSAGE },
        { skillName: SKILL_NAMES.VOCABULARY, measureName: MEASURE_NAMES.VOC },
    ],
    [IDEL2]: [
        { skillName: SKILL_NAMES.CONCIENCIA_FONOLOGICA, measureName: MEASURE_NAMES.FSS },
        { skillName: SKILL_NAMES.SONIDOS_DE_LETRAS, measureName: MEASURE_NAMES.FSL },
        { skillName: SKILL_NAMES.DECODIFICATION, measureName: MEASURE_NAMES.LSS },
        {
            skillName: SKILL_NAMES.LECTURA_DE_PALABRAS,
            measureName: MEASURE_NAMES.FEP,
        },
        { skillName: SKILL_NAMES.FLUIDEZ_EN_LA_LECTURA, measureName: MEASURE_NAMES.FLO },
        { skillName: SKILL_NAMES.COMPRENSION_BASICA, measureName: MEASURE_NAMES.CP },
    ],
};

export const APPLICATION_BY_SKILL_NAME = {
    ...Object.keys(PM_SKILL_AND_MEASURE_NAMES_BY_APPLICATION).reduce(
        (skillsByAppName, appKey) => ({
            ...PM_SKILL_AND_MEASURE_NAMES_BY_APPLICATION[appKey].reduce(
                (skillByAppName, { skillName }) => ({
                    ...skillByAppName,
                    [skillName]: appKey,
                }),
                skillsByAppName,
            ),
        }),
        {},
    ),
};

export const OPTIONAL_SKILL_NAMES = {
    [GRADE.KG]: [SKILL_NAMES.FIRST_SOUND_ISOLATION],
    [GRADE.TWO]: [SKILL_NAMES.LETTER_NAMES, SKILL_NAMES.PHONEMIC_AWARENESS],
};

export const MEASURE_SCORES = {
    LNF: 'LNF',
    PSF: 'PSF',
    NWF_CLS: 'NWF-CLS',
    NWF_WRC: 'NWF-WRC',
    WRF: 'WRF',
    ORF: 'ORF',
    ORF_ACC: 'ORF-ACC',
    MAZE: 'MAZE',
    TRC: 'TRC',
    OL: 'OL',
    DEC_IW: 'IW',
    DEC_RW: 'RW',
    DEC_LC: 'LC',
    DEC_AP: 'AP',
    CS_PASSAGE: 'CS-Passage',
    CS_LITERAL: 'CS-Literal',
    CS_INFERENCE: 'CS-Inference',
    VOC: 'VOC',
    FNL: 'FNL',
    FSLI: 'FSLI',
    FSLIK: 'FSLIK',
    FSL: 'FSL',
    FSS: 'FSS',
    QQ: 'QQ',
    FEP: 'FEP',
    FLO: 'FLO',
    FLO_ACC: 'FLO-ACC',
    PAB: 'PAB',
    FSI: 'FSI',
    LSS: 'LSS',
    CP: 'CLPL',
};

export const OPTIONAL_MEASURE_SCORES = {
    [DIBELS8]: {
        [GRADE.KG]: [MEASURE_SCORES.FSI],
        [GRADE.TWO]: [MEASURE_SCORES.LNF, MEASURE_SCORES.PSF],
    },
    [IDEL2]: {
        [GRADE.KG]: [MEASURE_SCORES.QQ],
        [GRADE.ONE]: [MEASURE_SCORES.QQ, MEASURE_SCORES.CP],
        [GRADE.TWO]: [MEASURE_SCORES.QQ, MEASURE_SCORES.FNL],
    },
};

export const CS_MEASURES = [
    MEASURE_NAMES.CS_PASSAGE,
    MEASURE_NAMES.CS_RETELL,
    MEASURE_NAMES.CS_RECALL,
    MEASURE_NAMES.CS_LITERAL,
    MEASURE_NAMES.CS_INFERENCE,
];

export const DEC_MEASURES = [
    MEASURE_NAMES.DEC_IW,
    MEASURE_NAMES.DEC_RW,
    MEASURE_NAMES.DEC_LC,
    MEASURE_NAMES.DEC_AP,
];

export const BURST_MEASURES = [
    MEASURE_NAMES.CS_RETELL,
    MEASURE_NAMES.CS_RECALL,
    MEASURE_NAMES.CS_LITERAL,
    MEASURE_NAMES.CS_INFERENCE,
    MEASURE_NAMES.DEC_IW,
    MEASURE_NAMES.DEC_RW,
    MEASURE_NAMES.DEC_LC,
    MEASURE_NAMES.DEC_AP,
    MEASURE_NAMES.VOC,
];

export const BURST_MEASURE_GENUS = [
    MEASURE_NAMES.CS,
    MEASURE_NAMES.DEC_IW,
    MEASURE_NAMES.DEC_RW,
    MEASURE_NAMES.DEC_LC,
    MEASURE_NAMES.DEC_AP,
    MEASURE_NAMES.VOC,
];

export const NECESSITY = {
    OPTIONAL: 'optional',
    NOT_ENOUGH_INFO: 'not_enough_info',
    REQUIRED: 'required',
    NOT_PERMITTED: 'not_permitted',
};

export const DIBELS8_MEASURES = [
    MEASURE_NAMES.LNF,
    MEASURE_NAMES.PSF,
    MEASURE_NAMES.NWF_CLS,
    MEASURE_NAMES.NWF_WRC,
    MEASURE_NAMES.WRF,
    MEASURE_NAMES.ORF,
    MEASURE_NAMES.ORF_ACC,
    MEASURE_NAMES.PAB,
    MEASURE_NAMES.FSI,
    MEASURE_NAMES.MAZE,
];

export const IDEL2_MEASURES = [
    MEASURE_NAMES.FNL,
    MEASURE_NAMES.FSLIK,
    MEASURE_NAMES.FSL,
    MEASURE_NAMES.LSS,
    MEASURE_NAMES.FSS,
    MEASURE_NAMES.QQ,
    MEASURE_NAMES.FEP,
    MEASURE_NAMES.FLO,
    MEASURE_NAMES.FLO_ACC,
    MEASURE_NAMES.CP,
];

export const MEASURE_SCORES_BY_APPLICATION = {
    [IDEL2]: [
        MEASURE_SCORES.FNL,
        MEASURE_SCORES.FSS,
        MEASURE_SCORES.QQ,
        MEASURE_SCORES.FSL,
        MEASURE_SCORES.FSLI,
        MEASURE_SCORES.FSLIK,
        MEASURE_SCORES.LSS,
        MEASURE_SCORES.FEP,
        MEASURE_SCORES.FLO,
        MEASURE_SCORES.FLO_ACC,
        MEASURE_SCORES.CP,
    ],
    [DIBELS8]: [
        MEASURE_SCORES.LNF,
        MEASURE_SCORES.PSF,
        MEASURE_SCORES.NWF_CLS,
        MEASURE_SCORES.NWF_WRC,
        MEASURE_SCORES.WRF,
        MEASURE_SCORES.ORF,
        MEASURE_SCORES.ORF_ACC,
        MEASURE_SCORES.MAZE,
        MEASURE_SCORES.TRC,
        MEASURE_SCORES.PAB,
        MEASURE_SCORES.FSI,
    ],
    [OLS]: MEASURE_SCORES.OL,
    [OLS_SPANISH]: MEASURE_SCORES.OL,
    [R3D]: MEASURE_SCORES.TRC,
    [R3DSPANISH]: MEASURE_SCORES.TRC,
};

export const GENRE_TYPES = {
    FICTION: 'fiction',
    NONFICTION: 'nonfiction',
};

export const SHORT_GENRE_TYPES = {
    [GENRE_TYPES.FICTION]: 'F',
    [GENRE_TYPES.NONFICTION]: 'NF',
};

export const BASIC_D8_MEASURES = [
    MEASURE_NAMES.LNF,
    MEASURE_NAMES.PSF,
    MEASURE_NAMES.NWF,
    MEASURE_NAMES.WRF,
    MEASURE_NAMES.ORF,
];
export const BASIC_IDEL2_MEASURES = [
    MEASURE_NAMES.FNL,
    MEASURE_NAMES.FSLI,
    MEASURE_NAMES.FSLIK,
    MEASURE_NAMES.FSL,
    MEASURE_NAMES.LSS,
    MEASURE_NAMES.FSS,
    MEASURE_NAMES.QQ,
    MEASURE_NAMES.FEP,
    MEASURE_NAMES.FLO,
    MEASURE_NAMES.CP,
];

export const MEASURE_BY_MEASURE_SCORE = {
    [MEASURE_SCORES.LNF]: MEASURE_NAMES.LNF,
    [MEASURE_SCORES.PSF]: MEASURE_NAMES.PSF,
    [MEASURE_SCORES.NWF_CLS]: MEASURE_NAMES.NWF,
    [MEASURE_SCORES.NWF_WRC]: MEASURE_NAMES.NWF,
    [MEASURE_SCORES.WRF]: MEASURE_NAMES.WRF,
    [MEASURE_SCORES.ORF]: MEASURE_NAMES.ORF,
    [MEASURE_SCORES.ORF_ACC]: MEASURE_NAMES.ORF,
    [MEASURE_SCORES.MAZE]: MEASURE_NAMES.MAZE,
    [MEASURE_SCORES.TRC]: MEASURE_NAMES.TRC,
    [MEASURE_SCORES.OL]: MEASURE_NAMES.OL,
    [MEASURE_SCORES.DEC_IW]: MEASURE_NAMES.DEC_IW,
    [MEASURE_SCORES.DEC_RW]: MEASURE_NAMES.DEC_RW,
    [MEASURE_SCORES.DEC_LC]: MEASURE_NAMES.DEC_LC,
    [MEASURE_SCORES.DEC_AP]: MEASURE_NAMES.DEC_AP,
    [MEASURE_SCORES.CS_PASSAGE]: MEASURE_NAMES.CS_PASSAGE,
    [MEASURE_SCORES.CS_LITERAL]: MEASURE_NAMES.CS_LITERAL,
    [MEASURE_SCORES.CS_INFERENCE]: MEASURE_NAMES.CS_INFERENCE,
    [MEASURE_SCORES.VOC]: MEASURE_NAMES.VOC,
    [MEASURE_SCORES.FNL]: MEASURE_NAMES.FNL,
    [MEASURE_SCORES.FSLI]: MEASURE_NAMES.FSLI,
    [MEASURE_SCORES.FSLIK]: MEASURE_NAMES.FSLIK,
    [MEASURE_SCORES.FSL]: MEASURE_NAMES.FSL,
    [MEASURE_SCORES.LSS]: MEASURE_NAMES.LSS,
    [MEASURE_SCORES.FSS]: MEASURE_NAMES.FSS,
    [MEASURE_SCORES.QQ]: MEASURE_NAMES.QQ,
    [MEASURE_SCORES.FEP]: MEASURE_NAMES.FEP,
    [MEASURE_SCORES.FLO]: MEASURE_NAMES.FLO,
    [MEASURE_SCORES.FLO_ACC]: MEASURE_NAMES.FLO,
    [MEASURE_SCORES.PAB]: MEASURE_NAMES.PAB,
    [MEASURE_SCORES.FSI]: MEASURE_NAMES.FSI,
    [MEASURE_SCORES.CP]: MEASURE_NAMES.CP,
};

// need this map in recommendations calculations
// it defines which measure score is relevant for every measure
export const MEASURE_SCORE_BY_RECOMMENDATION_MEASURE = {
    [DIBELS8]: {
        [MEASURE_NAMES.LNF]: MEASURE_SCORES.LNF,
        [MEASURE_NAMES.PSF]: MEASURE_SCORES.PSF,
        [MEASURE_NAMES.NWF]: MEASURE_SCORES.NWF_CLS,
        [MEASURE_NAMES.WRF]: MEASURE_SCORES.WRF,
        [MEASURE_NAMES.ORF]: MEASURE_SCORES.ORF,
    },
    [IDEL2]: {
        [MEASURE_NAMES.FNL]: MEASURE_SCORES.FNL,
        [MEASURE_NAMES.FSS]: MEASURE_SCORES.FSS,
        [MEASURE_NAMES.FSL]: MEASURE_SCORES.FSL,
        [MEASURE_NAMES.LSS]: MEASURE_SCORES.LSS,
        [MEASURE_NAMES.FEP]: MEASURE_SCORES.FEP,
        [MEASURE_NAMES.FLO]: MEASURE_SCORES.FLO,
        [MEASURE_NAMES.CP]: MEASURE_SCORES.CP,
    },
};

export const ON_TRACK = 'ON_TRACK';
export const OFF_TRACK = 'OFF_TRACK';

export const ON_TRACK_TEXT_POINTER = 'ON_TACK_TEXT_POINTER';
export const OFF_TRACK_TEXT_POINTER = 'OFF_TRACK_TEXT_POINTER';

export const RECOMMENDATION_STATUSES = {
    [DIBELS8]: {
        [ON_TRACK]: 'ON_TRACK',
        [OFF_TRACK]: 'OFF_TRACK',
    },
    [IDEL2]: {
        [ON_TRACK]: 'ON_TRACK',
        [OFF_TRACK]: 'OFF_TRACK',
    },
};

export const RECOMMENDATION_STATUSES_ICONS = {
    [ON_TRACK]: 'on-track',
    [OFF_TRACK]: 'off-track',
};

export const MEASURE_SCORE_BY_SKILL_NAME = {
    [SKILL_NAMES.LETTER_NAMES]: MEASURE_SCORES.LNF,
    [SKILL_NAMES.PHONEMIC_AWARENESS]: MEASURE_SCORES.PSF,
    [SKILL_NAMES.LETTER_SOUNDS]: MEASURE_SCORES.NWF_CLS,
    [SKILL_NAMES.DECODING]: MEASURE_SCORES.NWF_WRC,
    [SKILL_NAMES.WORD_READING]: MEASURE_SCORES.WRF,
    [SKILL_NAMES.READING_FLUENCY]: MEASURE_SCORES.ORF,
    [SKILL_NAMES.READING_ACCURACY]: MEASURE_SCORES.ORF_ACC,
    [SKILL_NAMES.BASIC_COMPREHENSION]: MEASURE_SCORES.MAZE,
    [SKILL_NAMES.TEXT_READING_COMPREHENSION]: MEASURE_SCORES.TRC,
    [SKILL_NAMES.IRREGULAR_WORDS]: MEASURE_SCORES.DEC_IW,
    [SKILL_NAMES.REGULAR_WORDS]: MEASURE_SCORES.DEC_RW,
    [SKILL_NAMES.LETTER_COMBINATIONS]: MEASURE_SCORES.DEC_LC,
    [SKILL_NAMES.ADVANCED_PHONEMES]: MEASURE_SCORES.DEC_AP,
    [SKILL_NAMES.RETELL_RECALL]: MEASURE_SCORES.CS_PASSAGE,
    [SKILL_NAMES.LITERAL]: MEASURE_SCORES.CS_LITERAL,
    [SKILL_NAMES.INFERENCE]: MEASURE_SCORES.CS_INFERENCE,
    [SKILL_NAMES.VOCABULARY]: MEASURE_SCORES.VOC,
    [SKILL_NAMES.ORAL_LANGUAGE]: MEASURE_SCORES.OL,
    [SKILL_NAMES.PHONOLOGICAL_AWARENESS_BLENDING]: MEASURE_SCORES.PAB,
    [SKILL_NAMES.FIRST_SOUND_ISOLATION]: MEASURE_SCORES.FSI,
    [SKILL_NAMES.LECTURA_DE_PALABRAS]: MEASURE_SCORES.FEP,
    [SKILL_NAMES.FLUIDEZ_EN_LA_LECTURA]: MEASURE_SCORES.FLO,
    [SKILL_NAMES.PRECISION_EN_LA_LECTURA]: MEASURE_SCORES.FLO_ACC,

    [SKILL_NAMES.NOMBRAR_LETRAS]: [MEASURE_SCORES.FNL],
    [SKILL_NAMES.SONIDOS_DE_LETRAS]: [
        MEASURE_SCORES.FSLI,
        MEASURE_SCORES.FSL,
        MEASURE_SCORES.FSLIK,
    ],
    [SKILL_NAMES.DECODIFICATION]: [MEASURE_SCORES.LSS],
    [SKILL_NAMES.CONCIENCIA_FONOLOGICA]: [MEASURE_SCORES.FSS, MEASURE_SCORES.QQ],
    [SKILL_NAMES.COMPRENSION_BASICA]: MEASURE_SCORES.CP,
};

export const MEASURE_BY_SKILL_NAME = {
    [SKILL_NAMES.LETTER_NAMES]: MEASURE_NAMES.LNF,
    [SKILL_NAMES.PHONEMIC_AWARENESS]: MEASURE_NAMES.PSF,
    [SKILL_NAMES.LETTER_SOUNDS]: MEASURE_NAMES.NWF,
    [SKILL_NAMES.DECODING]: MEASURE_NAMES.NWF,
    [SKILL_NAMES.WORD_READING]: MEASURE_NAMES.WRF,
    [SKILL_NAMES.READING_FLUENCY]: MEASURE_NAMES.ORF,
    [SKILL_NAMES.READING_ACCURACY]: MEASURE_NAMES.ORF,
    [SKILL_NAMES.BASIC_COMPREHENSION]: MEASURE_NAMES.MAZE,
    [SKILL_NAMES.TEXT_READING_COMPREHENSION]: MEASURE_NAMES.TRC,
    [SKILL_NAMES.IRREGULAR_WORDS]: MEASURE_NAMES.DEC_IW,
    [SKILL_NAMES.REGULAR_WORDS]: MEASURE_NAMES.DEC_RW,
    [SKILL_NAMES.LETTER_COMBINATIONS]: MEASURE_NAMES.DEC_LC,
    [SKILL_NAMES.ADVANCED_PHONEMES]: MEASURE_NAMES.DEC_AP,
    [SKILL_NAMES.RETELL_RECALL]: MEASURE_NAMES.CS_PASSAGE,
    [SKILL_NAMES.LITERAL]: MEASURE_NAMES.CS_LITERAL,
    [SKILL_NAMES.INFERENCE]: MEASURE_NAMES.CS_INFERENCE,
    [SKILL_NAMES.VOCABULARY]: MEASURE_NAMES.VOC,
    [SKILL_NAMES.ORAL_LANGUAGE]: MEASURE_NAMES.OL,
    [SKILL_NAMES.NOMBRAR_LETRAS]: MEASURE_NAMES.FNL,
    [SKILL_NAMES.SONIDOS_DE_LETRAS]: MEASURE_NAMES.FSL,
    [SKILL_NAMES.LECTURA_DE_PALABRAS]: MEASURE_NAMES.FEP,
    [SKILL_NAMES.FLUIDEZ_EN_LA_LECTURA]: MEASURE_NAMES.FLO,
    [SKILL_NAMES.PRECISION_EN_LA_LECTURA]: MEASURE_NAMES.FLO,
    [SKILL_NAMES.PHONOLOGICAL_AWARENESS_BLENDING]: MEASURE_NAMES.PAB,
    [SKILL_NAMES.FIRST_SOUND_ISOLATION]: MEASURE_NAMES.FSI,
    [SKILL_NAMES.COMPRENSION_BASICA]: MEASURE_NAMES.CP,
    [SKILL_NAMES.DECODIFICATION]: MEASURE_NAMES.LSS,
    [SKILL_NAMES.CONCIENCIA_FONOLOGICA]: MEASURE_NAMES.FSS,
};

export const AVAILABLE_SKILL_NAMES_BY_MEASURE_NAMES = {
    [MEASURE_NAMES.LNF]: [SKILL_NAMES.LETTER_NAMES],
    [MEASURE_NAMES.PSF]: [SKILL_NAMES.PHONEMIC_AWARENESS],
    [MEASURE_NAMES.NWF]: [SKILL_NAMES.LETTER_SOUNDS, SKILL_NAMES.DECODING],
    [MEASURE_NAMES.WRF]: [SKILL_NAMES.WORD_READING],
    [MEASURE_NAMES.ORF]: [SKILL_NAMES.READING_FLUENCY, SKILL_NAMES.READING_ACCURACY],
    [MEASURE_NAMES.MAZE]: [SKILL_NAMES.BASIC_COMPREHENSION],
    [MEASURE_NAMES.TRC]: [SKILL_NAMES.TEXT_READING_COMPREHENSION],
    [MEASURE_NAMES.DEC_IW]: [SKILL_NAMES.IRREGULAR_WORDS],
    [MEASURE_NAMES.DEC_RW]: [SKILL_NAMES.REGULAR_WORDS],
    [MEASURE_NAMES.DEC_LC]: [SKILL_NAMES.LETTER_COMBINATIONS],
    [MEASURE_NAMES.DEC_AP]: [SKILL_NAMES.ADVANCED_PHONEMES],
    [MEASURE_NAMES.CS_PASSAGE]: [SKILL_NAMES.RETELL_RECALL],
    [MEASURE_NAMES.CS_LITERAL]: [SKILL_NAMES.LITERAL],
    [MEASURE_NAMES.CS_INFERENCE]: [SKILL_NAMES.INFERENCE],
    [MEASURE_NAMES.VOC]: [SKILL_NAMES.VOCABULARY],
    [MEASURE_NAMES.OL]: [SKILL_NAMES.ORAL_LANGUAGE],
    [MEASURE_NAMES.FNL]: [SKILL_NAMES.NOMBRAR_LETRAS],
    [MEASURE_NAMES.FNL2]: [SKILL_NAMES.NOMBRAR_LETRAS],
    [MEASURE_NAMES.FSLI]: [SKILL_NAMES.SONIDOS_DE_LETRAS],
    [MEASURE_NAMES.FSLIK]: [SKILL_NAMES.SONIDOS_DE_LETRAS],
    [MEASURE_NAMES.FSL]: [SKILL_NAMES.SONIDOS_DE_LETRAS],
    [MEASURE_NAMES.QQ]: [SKILL_NAMES.CONCIENCIA_FONOLOGICA],
    [MEASURE_NAMES.LSS]: [SKILL_NAMES.DECODIFICATION],
    [MEASURE_NAMES.FSS]: [SKILL_NAMES.CONCIENCIA_FONOLOGICA],
    [MEASURE_NAMES.FEP]: [SKILL_NAMES.LECTURA_DE_PALABRAS],
    [MEASURE_NAMES.FLO]: [SKILL_NAMES.FLUIDEZ_EN_LA_LECTURA, SKILL_NAMES.PRECISION_EN_LA_LECTURA],
    [MEASURE_NAMES.PAB]: [SKILL_NAMES.PHONOLOGICAL_AWARENESS_BLENDING],
    [MEASURE_NAMES.FSI]: [SKILL_NAMES.FIRST_SOUND_ISOLATION],
    [MEASURE_NAMES.CP]: [SKILL_NAMES.COMPRENSION_BASICA],
};

export const SKILL_NAMES_BY_MEASURE = invertBy(MEASURE_BY_SKILL_NAME);

export const PM_MEASURES_BY_APPLICATION = mapValues(
    PM_SKILL_AND_MEASURE_NAMES_BY_APPLICATION,
    skillAndMeasureNames => skillAndMeasureNames.map(({ measureName }) => measureName),
);
export const SKILL_BY_BURST_MEASURE_NAME = {
    [MEASURE_NAMES.CS_RETELL]: SKILL_NAMES.RETELL_RECALL,
    [MEASURE_NAMES.CS_RECALL]: SKILL_NAMES.RETELL_RECALL,
    [MEASURE_NAMES.CS_LITERAL]: SKILL_NAMES.LITERAL,
    [MEASURE_NAMES.CS_INFERENCE]: SKILL_NAMES.INFERENCE,
    [MEASURE_NAMES.DEC_IW]: SKILL_NAMES.IRREGULAR_WORDS,
    [MEASURE_NAMES.DEC_RW]: SKILL_NAMES.REGULAR_WORDS,
    [MEASURE_NAMES.DEC_LC]: SKILL_NAMES.LETTER_COMBINATIONS,
    [MEASURE_NAMES.DEC_AP]: SKILL_NAMES.ADVANCED_PHONEMES,
    [MEASURE_NAMES.VOC]: SKILL_NAMES.VOCABULARY,
};

export const MEASURE_SCORE_DISPLAY_NAMES = {
    ...mapKeys(MEASURE_SCORES),
    [MEASURE_SCORES.CS_PASSAGE]: 'CS Retell/Recall',
    [MEASURE_SCORES.CS_LITERAL]: 'CS Literal',
    [MEASURE_SCORES.CS_INFERENCE]: 'CS Inference',
    [MEASURE_SCORES.ORF_ACC]: 'ORF-Accu',
    [MEASURE_SCORES.MAZE]: 'Maze',
    [MEASURE_SCORES.FNL]: 'FNL',
    [MEASURE_SCORES.FSLI]: 'FSL K-Inicio',
    [MEASURE_SCORES.FSLIK]: 'FSL K-Inicio',
    [MEASURE_SCORES.FSL]: 'FSL',
    [MEASURE_SCORES.LSS]: 'LSS',
    [MEASURE_SCORES.FSS]: 'FSS',
    [MEASURE_SCORES.QQ]: 'QQ',
    [MEASURE_SCORES.FLO_ACC]: 'FLO-Prec',
    [MEASURE_SCORES.PAB]: 'PA-B',
    [MEASURE_SCORES.CP]: 'CP',
};

export const D8_SUBMEASURES = [SKILL_NAMES.DECODING, SKILL_NAMES.READING_ACCURACY];

export const IDEL2_SUBMEASURES = [SKILL_NAMES.PRECISION_EN_LA_LECTURA];

export const ADMINISTRATION_SCHEDULE = {
    [GRADE.KG]: [
        SKILL_NAMES.LETTER_NAMES,
        SKILL_NAMES.PHONEMIC_AWARENESS,
        SKILL_NAMES.FIRST_SOUND_ISOLATION,
        SKILL_NAMES.LETTER_SOUNDS,
        SKILL_NAMES.DECODING,
        SKILL_NAMES.WORD_READING,
    ],
    [GRADE.ONE]: [
        SKILL_NAMES.LETTER_NAMES,
        SKILL_NAMES.PHONEMIC_AWARENESS,
        SKILL_NAMES.LETTER_SOUNDS,
        SKILL_NAMES.DECODING,
        SKILL_NAMES.WORD_READING,
        SKILL_NAMES.READING_FLUENCY,
        SKILL_NAMES.READING_ACCURACY,
    ],
    [GRADE.TWO]: [
        SKILL_NAMES.LETTER_NAMES,
        SKILL_NAMES.PHONEMIC_AWARENESS,
        SKILL_NAMES.LETTER_SOUNDS,
        SKILL_NAMES.DECODING,
        SKILL_NAMES.WORD_READING,
        SKILL_NAMES.READING_FLUENCY,
        SKILL_NAMES.READING_ACCURACY,
        SKILL_NAMES.BASIC_COMPREHENSION,
    ],
    [GRADE.THREE]: [
        SKILL_NAMES.LETTER_SOUNDS,
        SKILL_NAMES.DECODING,
        SKILL_NAMES.WORD_READING,
        SKILL_NAMES.READING_FLUENCY,
        SKILL_NAMES.READING_ACCURACY,
        SKILL_NAMES.BASIC_COMPREHENSION,
    ],
    [GRADE.FOUR]: [
        SKILL_NAMES.READING_FLUENCY,
        SKILL_NAMES.READING_ACCURACY,
        SKILL_NAMES.BASIC_COMPREHENSION,
    ],
    [GRADE.FIVE]: [
        SKILL_NAMES.READING_FLUENCY,
        SKILL_NAMES.READING_ACCURACY,
        SKILL_NAMES.BASIC_COMPREHENSION,
    ],
    [GRADE.SIX]: [
        SKILL_NAMES.READING_FLUENCY,
        SKILL_NAMES.READING_ACCURACY,
        SKILL_NAMES.BASIC_COMPREHENSION,
    ],
};

export const OBSERVATIONAL_SKILLS = mapValues(ADMINISTRATION_SCHEDULE, skills =>
    skills.filter(skill => skill !== SKILL_NAMES.BASIC_COMPREHENSION),
);

export const SUPPORTED_GRADES = [
    GRADE.KG,
    GRADE.ONE,
    GRADE.TWO,
    GRADE.THREE,
    GRADE.FOUR,
    GRADE.FIVE,
    GRADE.SIX,
];

export const SUPPORTED_GRADES_BY_APP = {
    [IDEL]: [GRADE.KG, GRADE.ONE, GRADE.TWO, GRADE.THREE],
    [IDEL2]: [GRADE.KG, GRADE.ONE],
    [OLS]: [GRADE.KG, GRADE.ONE, GRADE.TWO, GRADE.THREE, GRADE.FOUR, GRADE.FIVE],
    [OLS_SPANISH]: [GRADE.KG, GRADE.ONE, GRADE.TWO],
    [R3D]: SUPPORTED_GRADES,
    [R3DSPANISH]: SUPPORTED_GRADES,
    [DIBELS8]: SUPPORTED_GRADES,
    [BURST]: SUPPORTED_GRADES,
    [MATHCBM]: SUPPORTED_GRADES,
};

export const NO_ASSESS_ICON_SKILL_NAMES = [SKILL_NAMES.BASIC_COMPREHENSION];
export const TRC_TYPE = {
    RR: 'RR',
    PC: 'PC',
    RB: 'RB',
};

export const TRC_RESULT_PAGES = {
    RR_RESULT: 'trc-rr-result',
    PC_RB_RESULT: 'trc-subassess-result',
};

const DEC_AVAILABLE_GRADES = [GRADE.TWO, GRADE.THREE, GRADE.FOUR, GRADE.FIVE, GRADE.SIX];

const VOC_AVAILABLE_GRADES = [GRADE.KG, GRADE.ONE, GRADE.TWO, GRADE.THREE];

// amount of days need to be passed after last PM result, when PM measure becomes recommended again
export const PM_RECOMMENDATION_FREQUENCY = 1;

// Burst availability section
const getAvailabilityStatus = (measure, available, necessity) => ({
    available,
    complete: false,
    discontinued: false,
    measure,
    necessity: necessity || NECESSITY.NOT_ENOUGH_INFO,
});

export const BURST_AVAILABILITY = {
    ...SUPPORTED_GRADES.reduce(
        (availabilityByGrade, grade) => ({
            ...availabilityByGrade,
            [grade]: BM_PERIODS.reduce(
                (orderedPeriodMeasureTree, period) => ({
                    ...orderedPeriodMeasureTree,
                    [period]: SKILL_NAMES_BY_APPLICATION[BURST].reduce(
                        (availability, skillName) => {
                            const measure = MEASURE_BY_SKILL_NAME[skillName];
                            let isAvailable = false;
                            if (measure === MEASURE_NAMES.VOC) {
                                isAvailable = VOC_AVAILABLE_GRADES.includes(grade);
                                return {
                                    ...availability,
                                    [measure]: getAvailabilityStatus(
                                        measure,
                                        isAvailable,
                                        NECESSITY.OPTIONAL,
                                    ),
                                };
                            }
                            if (CS_MEASURES.includes(measure)) {
                                isAvailable = grade === GRADE.ONE && period !== PERIOD.BOY;
                                return {
                                    ...availability,
                                    [measure]: getAvailabilityStatus(measure, isAvailable),
                                };
                            }
                            if (DEC_MEASURES.includes(measure)) {
                                isAvailable =
                                    (grade === GRADE.ONE && period !== PERIOD.BOY) ||
                                    DEC_AVAILABLE_GRADES.includes(grade);
                                return {
                                    ...availability,
                                    [measure]: getAvailabilityStatus(measure, isAvailable),
                                };
                            }
                            return availability;
                        },
                        {},
                    ),
                }),
                {},
            ),
        }),
        {},
    ),
};

export const DIBELS_AVAILABILITY = {
    ...SUPPORTED_GRADES.reduce(
        (availabilityByGrade, grade) => ({
            ...availabilityByGrade,
            [grade]: BM_PERIODS.reduce(
                (orderedPeriodMeasureTree, period) => ({
                    ...orderedPeriodMeasureTree,
                    [period]: SKILL_NAMES_BY_APPLICATION[DIBELS8].filter(
                        skillName => !get(OPTIONAL_SKILL_NAMES, grade, []).includes(skillName),
                    ).reduce((availability, skillName) => {
                        const measure = MEASURE_BY_SKILL_NAME[skillName];
                        const isAvailable = ADMINISTRATION_SCHEDULE[grade].includes(skillName);
                        return {
                            ...availability,
                            [measure]: getAvailabilityStatus(
                                measure,
                                isAvailable,
                                NECESSITY.REQUIRED,
                            ),
                        };
                    }, {}),
                }),
                {},
            ),
        }),
        {},
    ),
};

export const SKILL_REPLACEMENT_MAP = {
    [MEASURE_NAMES.LSS]: {
        fromSkillName: SKILL_NAMES.CONCIENCIA_FONOLOGICA,
        toSkillName: SKILL_NAMES.DECODIFICATION,
    },
};

export const SKILL_REPLACEMENT_MAP_MEASURES = Object.keys(SKILL_REPLACEMENT_MAP);

export const SORTED_LOWEST_SKILL_NAMES = [
    MEASURE_NAMES.FSS,
    MEASURE_NAMES.FSL,
    MEASURE_NAMES.LSS,
    MEASURE_NAMES.FEP,
    MEASURE_NAMES.FLO,
];
